import { useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import { api, setAccessToken } from "../lib/api.js";
import { cn } from "../lib/utils.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSignInIntro } from "../hooks/useSignInIntro.js";
import { StardustBackground } from "../components/StardustBackground.js";
import { LoginHero } from "../components/LoginHero.js";
import { LoginIntro } from "../components/LoginIntro.js";
import { GlassButton } from "../components/ui/glass-button.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

interface LoginResponse {
  accessToken?: string;
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  user?: { id: string; email: string; role: string; firstName: string; lastName: string };
}

const SSO_ERRORS: Record<string, string> = {
  missing_state: "SSO session expired. Please try again.",
  bad_state: "SSO session invalid. Please try again.",
  exchange_failed: "Microsoft sign-in failed. Please try again.",
  no_email: "Your Microsoft account has no email address visible to this app.",
  account_disabled: "Your account has been disabled. Contact HR.",
  no_org_setup: "Organisation not yet configured. Contact HR.",
  not_registered: "Your account has not been added to HRMS yet. Ask HR to create your employee record, then sign in again.",
};

const ERROR_ID = "login-error";

/** Build-up classes for each sign-in field, timed on the login intro (label, then well). */
const FIELD_REVEAL = {
  email: { label: "login-intro-pop [--t:5.75]", well: "login-intro-pop-wipe [--t:5.87] [--r:14px]" },
  password: { label: "login-intro-pop [--t:6]", well: "login-intro-pop-wipe [--t:6.12] [--r:14px]" },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken } = useAuth();
  const intro = useSignInIntro();

  const ssoError = searchParams.get("sso_error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(
    ssoError ? (SSO_ERRORS[ssoError] ?? "Microsoft sign-in failed. Please try again.") : null
  );
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, string> = { email, password };
      if (mfaStep && mfaCode) payload["mfaCode"] = mfaCode;

      const { data } = await api.post<LoginResponse>("/auth/login", payload);

      if (data.mfaRequired) {
        setMfaStep(true);
        setLoading(false);
        return;
      }

      if (!data.accessToken) throw new Error("No access token");
      setAccessToken(data.accessToken);
      setToken(data.accessToken);

      if (data.mfaSetupRequired) {
        navigate("/setup-mfa", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        if (mfaStep) {
          setError("Invalid MFA code. Please check your authenticator app.");
        } else {
          setError("Invalid email or password.");
        }
      } else if (status === 423) {
        setError("Account is temporarily locked. Try again in 15 minutes.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleMicrosoftLogin() {
    window.location.href = "/api/auth/sso/azure";
  }

  return (
    <div
      className={cn(
        "login-page relative isolate grid min-h-screen grid-cols-1 items-center justify-items-center gap-10 bg-surface-50 p-4 lg:grid-cols-2 lg:items-stretch lg:content-center lg:justify-items-stretch lg:px-12 lg:pt-10 lg:pb-18",
        !intro.running && "login-intro-done"
      )}
      data-intro={intro.state}
    >
      <StardustBackground hoverScale={0.3} />
      <div className="login-intro-shell w-full max-w-[400px] lg:col-start-1 lg:justify-self-center">
        <div data-stardust-quiet className="login-glass-card w-full px-8 py-10 max-[480px]:px-[22px] max-[480px]:py-[34px] lg:flex lg:h-full lg:flex-col lg:justify-center">
          <span className="login-glass-water" aria-hidden="true" />
          <h1 className="login-intro-pop [--t:5.2] text-center text-lg font-semibold tracking-[-0.012em] text-[color:var(--ink)]">
            Sign in to your account
          </h1>

          <GlassButton
            variant="pill"
            className="login-intro-pop-wipe [--t:5.35] [--r:999px] mt-[29px]"
            onClick={handleMicrosoftLogin}
          >
            <MicrosoftIcon />
            Sign in with Microsoft
          </GlassButton>

          <div className="my-[22px] flex items-center gap-3">
            <span className="login-intro-grow-left [--t:5.6] h-px flex-1 bg-[linear-gradient(to_right,transparent,var(--rule))]" />
            <span className="login-intro-pop [--t:5.65] whitespace-nowrap text-xs text-[color:var(--ink-3)]">
              or continue with email
            </span>
            <span className="login-intro-grow-right [--t:5.6] h-px flex-1 bg-[linear-gradient(to_left,transparent,var(--rule))]" />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col" aria-busy={loading}>
            {!mfaStep ? (
              <div className="flex flex-col gap-5">
                <GlassField
                  id="email"
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  invalid={Boolean(error)}
                  busy={loading}
                  reveal={FIELD_REVEAL.email}
                />
                <GlassField
                  id="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  invalid={Boolean(error)}
                  busy={loading}
                  reveal={FIELD_REVEAL.password}
                  trailing={
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute inset-y-0 my-auto right-2 h-8 w-8 text-[color:var(--ink-3)] hover:bg-transparent hover:text-[color:var(--ink)] active:bg-transparent"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((shown) => !shown)}
                    >
                      {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col">
                <p className="m-0 mb-3 text-sm leading-normal text-[color:var(--ink-2)]">
                  Open <strong>Google Authenticator</strong> and enter the 6-digit code for your HRMS account.
                </p>
                <GlassField
                  id="mfaCode"
                  label="Authenticator Code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputClassName="text-2xl tracking-[8px] text-center"
                  invalid={Boolean(error)}
                  busy={loading}
                />
                <button
                  type="button"
                  onClick={() => { setMfaStep(false); setMfaCode(""); setError(null); }}
                  className="mt-2 w-fit cursor-pointer appearance-none bg-transparent p-0 text-[13px] text-[color:var(--ink-2)] underline underline-offset-2"
                >
                  ← Back to sign in
                </button>
              </div>
            )}

            {error && (
              <p id={ERROR_ID} role="alert" className="login-glass-alert mt-[18px] rounded-[14px] px-3.5 py-3 text-[13px] leading-normal">
                {error}
              </p>
            )}

            <GlassButton
              variant="cta"
              type="submit"
              disabled={loading}
              className={cn("login-intro-pop-btn [--t:6.35] mt-[22px]", loading && "disabled:cursor-progress")}
            >
              {loading && (
                <span
                  aria-hidden="true"
                  className="h-[15px] w-[15px] shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white"
                />
              )}
              {loading ? "Signing in…" : mfaStep ? "Verify →" : "Sign in"}
            </GlassButton>
          </form>

          <div className="login-intro-sweep" aria-hidden="true">
            <span className="login-intro-sweep-band" onAnimationEnd={intro.complete} />
          </div>
        </div>
      </div>

      <LoginHero badgeReady={!intro.running} />
      <LoginIntro markRef={intro.markRef} />
    </div>
  );
}

interface GlassFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  /** Turns the well's rim red and links the input to the sign-in error. */
  invalid: boolean;
  /** Dims the well while the sign-in request is in flight. The input stays enabled. */
  busy: boolean;
  inputClassName?: string;
  /** Build-up classes for the label and the well, from FIELD_REVEAL. */
  reveal?: { label: string; well: string };
  /** A control placed inside the well, at the field's right edge (the password eye). */
  trailing?: ReactNode;
}

function GlassField({ label, invalid, busy, inputClassName, reveal, trailing, ...input }: GlassFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={input.id} className={cn("mb-0 text-[13.5px] font-medium text-[color:var(--ink-2)]", reveal?.label)}>
        {label}
      </Label>
      <div
        className={cn("login-glass-well", reveal?.well)}
        data-invalid={invalid ? "true" : undefined}
        data-busy={busy ? "true" : undefined}
      >
        <Input
          variant="glass"
          className={cn(trailing && "pr-11", inputClassName)}
          aria-invalid={invalid}
          aria-describedby={invalid ? ERROR_ID : undefined}
          {...input}
        />
        {trailing}
      </div>
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
